import { getApiBaseUrl } from '../../utils/environment.mjs';
import axios from 'axios';

const API_URL = getApiBaseUrl();

const _axiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  }
});


// Get claim for editing
export const getClaimForEdit = async (claimType, claimId) => {
  try {
    const response = await axios.get(
      `${API_URL}/admin/claims/${claimType}/${claimId}/edit`,
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    const data = error.response?.data;
    const err = new Error(data?.message || error.message || 'Request failed');
    err.status = error.response?.status;
    throw err;
  }
};

// Update claim as admin
export const updateClaimAsAdmin = async (claimType, claimId, updateData) => {
  try {
    const response = await axios.put(
      `${API_URL}/admin/claims/${claimType}/${claimId}/update`,
      updateData,
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    const data = error.response?.data;
    const err = new Error(data?.message || error.message || 'Request failed');
    err.status = error.response?.status;
    throw err;
  }
};

// Get claim versions for comparison
export const getClaimVersions = async (claimType, claimId) => {
  try {
    const response = await axios.get(
      `${API_URL}/admin/claims/${claimType}/${claimId}/versions`,
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    const data = error.response?.data;
    const err = new Error(data?.message || error.message || 'Request failed');
    err.status = error.response?.status;
    throw err;
  }
};

// Process claim action (approve, reject, return)
export const processClaimAction = async (claimType, claimId, actionData) => {
  try {
    const response = await axios.put(
      `${API_URL}/admin/claims/${claimType}/${claimId}/process`,
      actionData,
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    const data = error.response?.data;
    const err = new Error(data?.message || error.message || 'Request failed');
    err.status = error.response?.status;
    throw err;
  }
};

// Get all claims for admin dashboard
export const getAllClaimsForAdmin = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams(filters).toString();
    const response = await axios.get(
      `${API_URL}/admin/claims${queryParams ? `?${queryParams}` : ''}`,
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    const data = error.response?.data;
    const err = new Error(data?.message || error.message || 'Request failed');
    err.status = error.response?.status;
    throw err;
  }
};

// Get admin dashboard statistics
export const getAdminDashboardStats = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams(filters).toString();
    const response = await axios.get(
      `${API_URL}/admin/dashboard/stats${queryParams ? `?${queryParams}` : ''}`,
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    const data = error.response?.data;
    const err = new Error(data?.message || error.message || 'Request failed');
    err.status = error.response?.status;
    throw err;
  }
};

// Bulk delete draft claims (admin)
// claims = [{ id, claimType }]
export const bulkProcessClaims = async (claimIds, action, comments = '') => {
  try {
    const response = await axios.put(
      `${API_URL}/admin/claims/bulk-process`,
      {
        claimIds,
        action,
        comments
      },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    const data = error.response?.data;
    const err = new Error(data?.message || error.message || 'Request failed');
    err.status = error.response?.status;
    throw err;
  }
};

// Export admin actions history
export const getAdminActionsHistory = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams(filters).toString();
    const response = await axios.get(
      `${API_URL}/admin/actions/history${queryParams ? `?${queryParams}` : ''}`,
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    const data = error.response?.data;
    const err = new Error(data?.message || error.message || 'Request failed');
    err.status = error.response?.status;
    throw err;
  }
};

// Sanitize filename components to prevent path traversal and XSS
const sanitizeFilenameComponent = (input) => {
  if (typeof input !== 'string') return 'unknown';
  // Remove any characters that could be dangerous in filenames
  return input.replace(/[^a-zA-Z0-9_-]/g, '_').substring(0, 50);
};

// Download claim report
export const downloadClaimReport = async (claimType, claimId, format = 'pdf') => {
  try {
    // Validate format to only allow safe extensions
    const allowedFormats = ['pdf', 'xlsx', 'csv'];
    const safeFormat = allowedFormats.includes(format) ? format : 'pdf';

    // Sanitize claimId and claimType for URL and filename
    const safeClaimId = sanitizeFilenameComponent(String(claimId));
    const safeClaimType = sanitizeFilenameComponent(String(claimType));

    const response = await axios.get(
      `${API_URL}/admin/claims/${encodeURIComponent(safeClaimType)}/${encodeURIComponent(safeClaimId)}/report?format=${safeFormat}`,
      {
        withCredentials: true,
        responseType: 'blob',
        headers: {
          'Accept': safeFormat === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      }
    );

    // Create download link with sanitized filename
    // Security Note: This is NOT a DOM XSS vulnerability (Snyk false positive).
    // - The link.href is set to a blob URL created from binary file data (not HTML)
    // - The download attribute uses sanitized values (safeClaimId, safeFormat)
    // - No user content is injected into the DOM as HTML
    // - This is a standard browser file download pattern
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    // snyk:ignore javascript/DOMXSS - Blob URL from binary PDF data with sanitized filename; not exploitable as XSS
    link.setAttribute('download', `claim-${safeClaimId}.${safeFormat}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return { success: true };
  } catch (error) {
    const data = error.response?.data;
    const err = new Error(data?.message || error.message || 'Request failed');
    err.status = error.response?.status;
    throw err;
  }
};




const adminMonthlyReportService = {
  getAdminMonthlyReports: async (filters) => {
    try {
      const response = await axios.get(`${API_URL}/monthly-reports/submitted`, {
        params: filters,
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      const data = error.response?.data;
    const err = new Error(data?.message || error.message || 'Request failed');
    err.status = error.response?.status;
    throw err;
    }
  },

  getReportDetails: async (reportId) => {
    try {
      const response = await axios.get(`${API_URL}/monthly-reports/monthly-report/${reportId}`, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      const data = error.response?.data;
    const err = new Error(data?.message || error.message || 'Request failed');
    err.status = error.response?.status;
    throw err;
    }
  },

  // Drafts only - the server refuses anything that has been submitted.
  deleteDraftReport: async (reportId) => {
    try {
      const response = await axios.delete(`${API_URL}/monthly-reports/delete/report/${reportId}`, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      const data = error.response?.data;
      const err = new Error(data?.message || error.message || 'Request failed');
      err.status = error.response?.status;
      throw err;
    }
  }
};

export default adminMonthlyReportService;

